import type { ContextTicketDto } from '../views/HomeView/HomeView';

/**
 * Firm-wide case list (Lloyd, Aug 7 — open and closed cases on the main menu, sorted).
 *
 * Every part of the filtering and ordering happens on the server. That is the whole point: the
 * screens that came before this computed open/closed in the browser over whichever cases happened
 * to be loaded, so they could show whether *one client's* case was closed but could never answer
 * "show me every open case".
 */

export type CaseListState = 'open' | 'closed' | 'all';
export type CaseListSort =
  | 'courtDate'
  | 'lastActivity'
  | 'clientName'
  | 'created';
export type CaseListSortDir = 'asc' | 'desc';

export const CASE_LIST_PAGE_SIZE = 50;

/** ContextTicketDto plus the activity timestamp the server sorted on. */
export interface CaseListItem extends ContextTicketDto {
  lastActivityAt: string | null;
  /**
   * Does the court have a future setting for this case?
   *
   * Feeds the stage floor in deriveStageLabel, so a docketed case stops reading "1. Pending
   * payment" in the list (Lloyd, Aug 31 — Amadu Kamara). Optional so an older server response
   * simply falls back to the previous behaviour rather than rendering undefined.
   */
  hasFutureCourtEvent?: boolean;
}

export interface CaseListResponse {
  cases: CaseListItem[];
  total: number;
  from: number;
  size: number;
  state: CaseListState;
  sortBy: CaseListSort;
  sortDir: CaseListSortDir;
}

export interface FetchCaseListOptions {
  state: CaseListState;
  sortBy: CaseListSort;
  sortDir: CaseListSortDir;
  search?: string;
  from?: number;
  size?: number;
}

export async function fetchCaseList(
  options: FetchCaseListOptions,
): Promise<CaseListResponse> {
  const params = new URLSearchParams();
  params.set('state', options.state);
  params.set('sortBy', options.sortBy);
  params.set('sortDir', options.sortDir);
  params.set('from', String(options.from ?? 0));
  params.set('size', String(options.size ?? CASE_LIST_PAGE_SIZE));
  if (options.search?.trim()) {
    params.set('search', options.search.trim());
  }

  const response = await fetch(
    `${process.env.API_HOST || ''}/api/v1/manager/cases?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch cases: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}
